
  import React, { useState, useEffect } from 'react';
  import { useLazyQuery, gql } from '@apollo/client';
  import './ProductSearch.css';
  import { OutlinedInputField } from './pages/components/OutlinedInputField';
  import { Card, CardContent, Typography, Pagination, CircularProgress } from '@mui/material';


  const ProductSearch = ({ onSelect, onRemoveFromSelectedProducts, selectedProducts, ...otherProps }) => {
    const [pagination, setPagination] = useState({
      page: 1,
      totalPages: 1,
      totalCount: 0
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const pageSize = 5;

    const SEARCH_PRODUCTS_QUERY = gql`
      query SearchProducts($keyword: String!, $page: Int!, $pageSize: Int!) {
        searchProducts(keyword: $keyword, page: $page, pageSize: $pageSize) {
          totalCount
          products {
            product
            thumbnailUrl
            numberOfTransactions
            cashPrice
            storeCreditPrice
            requiresReview
          }
        }
      }
    `;

    const [searchProducts, { loading, data }] = useLazyQuery(SEARCH_PRODUCTS_QUERY, {
      fetchPolicy: 'network-only', // NEW
    });

    useEffect(() => {
      // Execute code only once when the component mounts
      searchProducts({ variables: { keyword: '', page: 1, pageSize: pageSize } });
    }, []); // Empty dependency array

    const loadPage = (newPage) => {
      if (newPage <= pagination.totalPages) {
        setPagination((prevPagination) => ({
          ...prevPagination,
          page: newPage,
        }));

        searchProducts({
          variables: { keyword: searchTerm, page: newPage, pageSize },
        });
      }
    };

    useEffect(() => {
      if (!data) return; // NEW

      const products = data.searchProducts?.products || []; // NEW
      const totalCount = data.searchProducts?.totalCount || 0; // NEW

      console.log("DATA: ", totalCount);

      setResults(products); // NEW
      setLoadingMore(false); // NEW
      setHasMore(products.length === pageSize); // NEW

      setPagination((prevPagination) => ({
        ...prevPagination,
        totalPages: totalCount === 0 ? 1 : Math.ceil(totalCount / pageSize), // NEW
        totalCount: totalCount, // NEW
      }));
    }, [data]);

    const handleSearch = (event) => {
      const value = event.target.value; // NEW
      setSearchTerm(value); // NEW

      setHasMore(true); // NEW
      setPagination({ // NEW
        page: 1,
        totalPages: 1,
        totalCount: 0,
      });

      if (value.trim() === '') { // NEW
        setResults([]); // NEW
        return; // NEW
      }

      searchProducts({
        variables: { keyword: value, page: 1, pageSize },
      }); // NEW
    };

    const addToSelectedProducts = (product) => {
      onSelect(product);
    };

    const removeFromSelectedProducts = (product) => {
      onRemoveFromSelectedProducts(product);
    };

    const renderPageButtons = () => {
      const buttons = [];
      const maxButtonsToShow = 5;

      for (let i = pagination.page - Math.floor(maxButtonsToShow / 2); i <= pagination.page + Math.floor(maxButtonsToShow / 2); i++) {
        if (i >= 1 && i <= pagination.totalPages) {
          buttons.push(
            <button
              key={i}
              onClick={() => loadPage(i)}
              disabled={i === pagination.page}
              className={i === pagination.page ? 'active' : ''}
            >
              {i}
            </button>
          );
        }
      }

      return buttons;
    };

    const renderSearchLabel = () => {
      // NEW: don't show while loading or no results
       if (loading || pagination.totalCount === 0) return null;
      return (
        <div className="search-label">
          {pagination.totalPages && (
            <p>
              Showing {pagination.totalCount.toLocaleString()} Results{" "}
              {searchTerm !== "" && (
                <span>
                  for <b>&ldquo;{searchTerm}&rdquo;</b>
                </span>
              )}
            </p>
          )}
        </div>
      );
    };

    const handleItemClick = (product) => {
      onSelect(product);
      setSearchTerm('');
      setResults([]);
      setPagination({
        page: 1,
        totalPages: 1,
        totalCount: 0
      });
    };

    const renderSearchResults = () => {
      console.log("LOADING: ", loading);

      if (loading) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '382.5px' }}>
            <CircularProgress size={24} />
          </div>
        );
      }

      if (!loading && results.length === 0 && searchTerm.trim() !== '') { // NEW
        return ( // NEW
          <div style={{ padding: '16px', textAlign: 'center', color: 'black' }}> {/* NEW */}
            No products found. {/* NEW */}
          </div> // NEW
        ); // NEW
      }

      return results.map((p, index) => ( // NEW
        <div
          key={`${p.product}-${index}`} // NEW
          className="product-search-result-item"
          onClick={() => handleItemClick(p)}
        >
          <img src={p.thumbnailUrl} style={{ width: 40, height: 40 }} />
          <span>{p.product} </span>
        </div>
      ));
    };

    const renderPagination = () => {
      if (results.length == 0) { return; }

      const start = (pagination.page - 1) * pageSize + 1;
      const end = Math.min(start + pageSize - 1, pagination.totalCount);

      return (
        <div
          className="product-search-result-pagination"
          style={{
            backgroundColor: '#ffffff',
            padding: '10px',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '16px',
            minHeight: '50px'
          }}
        >
          <i className="pagination-label" style={{ marginBottom: '8px', color: 'black' }}>
            Showing {start}-{end} of {pagination.totalCount} Results
          </i>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={(event, value) => loadPage(value)}
            siblingCount={2}
            boundaryCount={0}
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'black',
                backgroundColor: 'white'
              },
              '& .MuiPaginationItem-root:focused': {
                color: 'black',
                backgroundColor: 'white'
              },
              '& .MuiPaginationItem-root:hover': {
                backgroundColor: 'rgba(0,0,0,.1)'
              },
              '& .Mui-selected': {
                backgroundColor: 'rgba(0,198,255,1) !important',
                color: 'white',
              },
            }}
          />
        </div>
      );
    };

    return (
      <div className="product-search-container" {...otherProps}>
        <OutlinedInputField
          label="Search for Product / Brand"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearch}
          fullWidth
          autoComplete='off'
        />
        {searchTerm != undefined && searchTerm != '' ? (
          <div className="product-search-results">
            {renderSearchResults()}
            {renderPagination()}
          </div>
        ) : null}
      </div>
    );
  };

  export default ProductSearch;
